import { useCallback, useMemo, useState } from "react";
import { DropdownButton } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Button from "../common/components/Button";
import CustomForm from "../common/components/CustomForm";
import DataTable from "../common/components/DataTable";
import { DefaultButton } from "../common/components/DefaultButton";
import useClientConfig from "../common/hooks/clientConfigHook";
import useNetwork from "../common/hooks/networkHook";
import { insertJobForm, updateJobForm, useJobFormDefs, useJobForms } from "../reducers/jobCustomFormSlice";
import { CustomFormPrintingFactory } from "../common/utils/customFormPrinting";
import PropTypes from 'prop-types';
import { Col, Row } from "../common/components/Grid";
import DropdownItem from "react-bootstrap/esm/DropdownItem";
import Icon from "../common/components/Icon";
import { cdate, now } from "../common/utils/CDate";
import { insertCrewForm, updateCrewForm, useCrewFormDefs, useCrewForms } from '../reducers/crewCustomFormSlice';
import { genDocDefinition } from '../utils/printingUtilities';
import { useCrew } from "../reducers/crewSlice";
import { useRealizationContext } from "../common/reducers/utilities";
import { deepClone } from "../common/utils/objectUtilities";
import { useSpecializedTranslation } from "../hooks/specializedTranslationHook";
import { Modal } from "../common/utils/dialogUtlities";
import { useJob } from "../reducers/jobSlice";

const DEFAULT_SORT = ['-displayid'];

export const Forms = ({ uuid, forms, defs, disabled, onCreate, emptyMessage, onUpdate, style }) => {
    const { t, i18n } = useTranslation();
    const realizationContext = useRealizationContext();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    
    const [viewForm, setViewForm] = useState(null);

    const printForm = useCallback((form) => {
        const formSpec = defs.find(d => d.id ===  form?.formid);
        CustomFormPrintingFactory(realizationContext, i18n.resolvedLanguage, genDocDefinition).printForm( form.id, uuid,  { ...form.form, jobid: form.jobid, id: form.id }, formSpec, ( form.created ? ' created ' + cdate(form.created, clientConfig.tz)?.dateTimeFormat(clientConfig.timezones?.enable) : null)).then(doc => {
            const filename =  uuid.toString().replace('-', '') + '_' + formSpec.title + '_' + form.id + '_' + now(clientConfig.tz).fileName(clientConfig.timezones?.enable) + '.pdf';
            network.downloadBlob(new Blob([doc.data], { type: doc.mime }), filename + '.' + doc.type);
        })
    }, [realizationContext, network, i18n, uuid, defs, clientConfig.timezones?.enable, clientConfig.tz]);

    const tableDef = useMemo(() => [
        { title: t('customFormID'), type: "number", editable: false, readonly: true, field: "displayid" },
        { title: t('customFormType'), type: "text", editable: false, readonly: true, field: "formtype" },
        { title: t('created'), type: 'datetime', editable: false, readonly: true, field: 'timestamp' },
        { title: "", key: 'del', type: "dynamic", editable: false, readonly: true, def: (value, disabled, hideLabel, onChange) => <div className="w-100 text-right">
            <DefaultButton className="mx-2" kind={disabled || value.completed ? 'view' : 'edit'} onClick={() => setViewForm(deepClone(value))}></DefaultButton>
            <DefaultButton kind="print" onClick={() => printForm(deepClone(value))}></DefaultButton>
        </div> }
    ], [t, printForm]);

    const completeForm = useCallback((form) => {
        if (disabled) {
            return;
        }
        if (form.id) {
            onUpdate({ id: form.id, form: form.form, completed: now(clientConfig.tz).toISOString() });
        } else {
            onCreate({ ...form, completed: now(clientConfig.tz).toISOString() });
        }
        setViewForm(null);
    }, [onUpdate, onCreate, clientConfig.tz, disabled]);

    const saveForm = useCallback(form => {
        if (disabled) {
            return;
        }
        if (form.id) {
            onUpdate({ id: form.id, form: form.form });
        } else {
            onCreate({ ...form });
        }
        setViewForm(null);
    }, [onUpdate, onCreate, disabled]);

    return <>
    <Modal show={!!viewForm} size="xlg">
        <Modal.Header>
            {defs.find(d => d.id === viewForm?.formid)?.title}
        </Modal.Header>
        <Modal.Body>
            <div style={{ height: 'calc(100dvh - 180px)', overflowY: 'auto'}}>
                <CustomForm def={defs.find(d => d.id === viewForm?.formid)?.form} data={viewForm?.form} disabled={disabled || !!viewForm?.completed} onChange={delta => setViewForm({...viewForm, form: { ...viewForm.form, ...delta } })}></CustomForm>
            </div>
        </Modal.Body>
        <Modal.Footer>
            {viewForm?.id ? <DefaultButton kind="print" onClick={() => printForm(viewForm)}></DefaultButton> : null}
            {!disabled && !viewForm?.completed && <Button variant="success" confirmMessage={t('confirmFormComplete')} onClick={() => completeForm(viewForm)}><Icon variant="solid" kind="check"></Icon> {t('complete')}</Button>}
            {!disabled && !viewForm?.completed && <DefaultButton kind="save" onClick={() => saveForm(viewForm)}></DefaultButton>}
            <DefaultButton kind="close" onClick={() => setViewForm(null)}></DefaultButton>
        </Modal.Footer>
    </Modal>
    <Row>
        <Col>
            <DataTable columns={tableDef} disabled={true} value={forms} defaultSort={DEFAULT_SORT} emptyMessage={emptyMessage} style={style}></DataTable>
        </Col>
    </Row>
    <Row>
        <Col style={{ textAlign: 'end' }}>
            <DropdownButton variant="primary" title={t('formType')}>
                {defs.map(d => <DropdownItem key={'formdef_' + d.id} onClick={() => setViewForm({ formid: d.id, form: {} })}>{d.title}</DropdownItem>)}
            </DropdownButton>
        </Col>
    </Row>
    </>
}

export const JobForms = ({ jobid, disabled = false, style }) => {
    const forms = useJobForms(jobid);
    const job = useJob(jobid);
    const { t } = useSpecializedTranslation(job);
    const defs = useJobFormDefs();
    const network = useNetwork();

    const displayForms = useMemo(() => forms?.map(f => ({ ...f, displayid: f.id, jobid: job?.displayid })) ?? [], [forms, job]);

    return <Forms uuid={job?.displayid} forms={displayForms} defs={defs} disabled={disabled} style={style} onCreate={form => insertJobForm(network, { ...form, jobid })} 
            emptyMessage={t('emptyJobForms')} onUpdate={delta => updateJobForm(network, {...delta, jobid })}></Forms>
}

JobForms.propTypes = {
    /**
     * The jobid of the job the forms are associated with
     */
    jobid: PropTypes.string,
     /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,
    /**
     * Style object for the enclosed DataTable
     */
    style: PropTypes.object
}

export const ScheduledJobForms = ({ job, disabled, style, onChange }) => {
    const { t } = useSpecializedTranslation(job);
    const defs = useJobFormDefs();
    
    return <Forms uuid={job.scheduleid} forms={job.forms ?? []} defs={defs} disabled={disabled} style={style}
        onCreate={form => onChange({forms: [ ...job.forms, form ]})} emptyMessage={t('emptyJobForms')}
        onUpdate={delta => onChange({ forms: [ ...job.forms.filter(f => f.id !== delta.id), { ...job.forms.find(f => f.id === delta.id), delta }]})}>
    </Forms>
}

ScheduledJobForms.propTypes = {
    /**
     * The scheduleid of the scheduled job the forms are associated with
     */
    job: PropTypes.object,
    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,
    /**
     * Style object for the enclosed DataTable
     */
    style: PropTypes.object,
    /**
     * Function to call when a change happens
     */
    onChange: PropTypes.func
}

export const CrewForms = ({ callsign, disabled, style }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const crew = useCrew(callsign);
    const defs = useCrewFormDefs();
    const forms = useCrewForms(callsign);

    const displayForms = useMemo(() => forms?.map(f => ({ ...f, callsign: crew?.displayid })) ?? [], [crew, forms]);

    return <Forms uuid={crew.displayid} forms={displayForms} defs={defs} disabled={disabled} style={style} emptyMessage={t('emptyCrewForms')}
        onCreate={form => insertCrewForm(network, { callsign, ...form })}
        onUpdate={delta => updateCrewForm(network, delta)}></Forms>
}